import { HttpParams } from '@angular/common/http'
import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges
} from '@angular/core'

import { environment } from '../../../../environments/environment'
import { Role } from '../../../common/enums/role.enum'
import { Sample } from '../../../common/interfaces/sample.interface'
import { ResourceService } from '../../../common/services/resource.service'
import { ViewportService } from '../../../common/services/viewport.service'
import { AuthService } from '../../../common/services/auth.service'
import { ConnectivityService } from '../../../common/services/offline/connectivity.service'
import { User } from '../../../common/interfaces/user.interface'

@Component({
  selector: 'app-sample-table',
  templateUrl: './sample-table.component.html',
  styleUrls: ['./sample-table.component.scss'],
  providers: [ResourceService]
})
export class SampleTableComponent implements OnInit, OnChanges {
  @Input() samples: Sample[]
  @Input() showMission = true
  // Opt-in "select all displayed samples" button (used by the mission detail).
  @Input() showSelectAll = false
  sampleToDelete: Sample

  selectedSampleIds: number[] = []

  showDownloadButton: boolean
  showDownloadMenu: boolean = false;
  activeActionDropdown: number
  activeTouchActionBar: number
  isTouchResolution: boolean
  currentUser: User

  storagePath: string = environment.storagePath
  Role = Role
  isOnline = true

  constructor(
    private resourceService: ResourceService,
    private viewportService: ViewportService,
    private authService: AuthService,
    private connectivityService: ConnectivityService,
    private elementRef: ElementRef
  ) {}

  ngOnInit() {
    this.connectivityService.isOnline$.subscribe(online => {
      this.isOnline = online
    })

    this.viewportService.isTouchResolution.subscribe(
      (isTouchResolution: boolean) => {
        this.isTouchResolution = isTouchResolution
      }
    )

    this.authService.currentUser.subscribe((userRes: User) => {
      this.currentUser = userRes
    })
  }

  // Reset the selection whenever the displayed samples change (e.g. the mission
  // detail filter switched category/subcategory, or a new page was loaded).
  ngOnChanges(changes: SimpleChanges) {
    if (changes.samples && !changes.samples.firstChange) {
      this.resetSelectedSamples()
    }
  }

  isSelected(sampleId: number): boolean {
    return this.selectedSampleIds.includes(sampleId)
  }

  // Selectable samples: real (persisted) ids only — offline temp ids can't be exported.
  private selectableSampleIds(): number[] {
    return (this.samples || [])
      .map((sample: Sample) => sample.id)
      .filter((id: any) => typeof id === 'number') as number[]
  }

  get allSelected(): boolean {
    const selectable = this.selectableSampleIds()
    return (
      selectable.length > 0 &&
      selectable.every((id: number) => this.selectedSampleIds.includes(id))
    )
  }

  // Select every displayed (selectable) sample, or clear the selection if all
  // are already selected.
  toggleSelectAll() {
    if (this.allSelected) {
      this.resetSelectedSamples()
    } else {
      this.selectedSampleIds = this.selectableSampleIds()
      this.showDownloadButton = !!this.selectedSampleIds.length
    }
  }

  toggleSelect(sampleId: number) {
    if (this.selectedSampleIds.includes(sampleId)) {
      const index = this.selectedSampleIds.indexOf(sampleId)
      this.selectedSampleIds.splice(index, 1)
    } else {
      this.selectedSampleIds.push(sampleId)
    }

    // Display download button if sample(s) are selected
    this.showDownloadButton = !!this.selectedSampleIds.length
  }

  // Download Sample's Weighings file or ZIP folder with images
  download(type: string, sampleId?: number) {
    let params = new HttpParams()

    if (sampleId) {
      params = params.set('sampleIds', sampleId.toString())
    } else {
      this.selectedSampleIds.forEach((selectedSampleId: number) => {
        params = params.append('sampleIds', selectedSampleId.toString())
      })
    }

    if (type === 'images') {
      params = params.set('extractImages', 'true')
    } else if (type === 'weighings') {
      params = params.set('toFile', 'true')
    }

    const fallback = type === 'images' ? 'samples-images.zip' : 'samples-export.xlsx'
    this.resourceService.download('samples', params, fallback).subscribe()
  }

  toggleActionDropdown(index: number) {
    if (this.activeActionDropdown === index) {
      delete this.activeActionDropdown
    } else {
      this.activeActionDropdown = index
    }
  }

  toggleTouchActionBar(index: number) {
    if (this.activeTouchActionBar === index) {
      delete this.activeTouchActionBar
    } else {
      this.activeTouchActionBar = index
    }
  }

  @HostListener('document:click', ['$event.target'])
  onClick(target) {
    // We track clicks to close dropdown if open and click outside
    if (this.activeActionDropdown >= 0) {
      const dropdowns: NodeList = this.elementRef.nativeElement.querySelectorAll(
        '.dropdown'
      )

      let clickedOut = true
      dropdowns.forEach((d: Node) => {
        if (d.contains(target)) {
          clickedOut = false
        }
      })

      if (clickedOut) {
        delete this.activeActionDropdown
      }
    }
  }

  resetSelectedSamples() {
    this.selectedSampleIds = [];
    this.showDownloadButton = false;
  }

  isOfflineCreated(sample: Sample): boolean {
    return !!sample && typeof sample.id !== 'number' && String(sample.id).startsWith('temp_')
  }
}
